import axios, { AxiosResponse } from 'axios'
import hash from 'object-hash'
import { identity } from 'ramda'

import { RequestModifier } from '../'
import {
  CompassAPIDataPoint,
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getCompassAPIChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  getMultipleLineStrCategoriesChartProps,
  magnitudePointFormatter,
  tooltipFormatterMinMaxMonthly,
} from '../utils/compass'
import { defaultMinMaxIndicators, range6m1yAll } from '../utils/config'
import { Palette, chartPalette, datetimeXAxis } from '../utils/constants'
import type { BaseContext } from './'
import { CommonChartConfig, commonChartOptions } from './common'

const HOMEBUILDERS_CHARTS_PATH = '/api/charts/homebuilders'

export namespace Homebuilders {
  export namespace Funding {
    const CONTEXT_ID = 'equity:homebuilders:funding'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'net_funding'
        | 'balance'
        | 'balance_yoy'
        | 'savings_funding_returns'
        | 'cetip_stock'
        | 'cetip_stock_yoy'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'net_funding':
            return getNetFundingChartProps(config, data)
          case 'balance':
          case 'balance_yoy':
          case 'cetip_stock':
          case 'cetip_stock_yoy':
            return getLineChartProps(config, data)
          case 'savings_funding_returns':
            return getCategoriesChartProps(config, data)
        }
      },
    }

    const getNetFundingChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineStrCategoriesChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          convertToPercentages: Array(data.data.length).fill(true),
        },
        {
          color: chartPalette.slice(0, data.data.length).reverse(),
          lineWidth: data.data.map((_, idx) =>
            idx === data.data.length - 1 ? 3 : 1.25
          ),
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)

    const getLineChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        ...(config.chart === 'balance_yoy' || config.chart === 'cetip_stock_yoy'
          ? { convertToPercentages: Array(data.data.length).fill(true) }
          : {}),
        tooltip: { xDateFormat: '%B %Y' },
      })(config, data)

    const getCategoriesChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        data: convertDataToHighchartsFormat(s.data, {
          convertPercentage: idx < 2,
        }),
        color:
          idx === 0
            ? Palette.darkBlue
            : idx === 1
            ? Palette.darkGreen
            : Palette.fadedGreen,
        dashStyle: idx === 1 ? 'LongDash' : 'Solid',
        id: `${config.chart}-${idx}`,
        type: idx === 2 ? 'column' : 'spline',
        marker: { enabled: false },
        name: s.name,
        tooltip: {
          valueDecimals: 2,
          valueSuffix: idx > 1 ? 'bi' : '%',
          xDateFormat: '%Y',
        },
        yAxis: idx > 1 ? 'absolute' : 'relative',
        ...(idx === 2 ? { pointPadding: 0, groupPadding: 0 } : {}),
      }))

      const yAxis = [
        {
          id: 'absolute',
          opposite: false,
          title: { text: 'Bilhões' },
        },
        {
          id: 'relative',
          labels: { format: '{value}%' },
          opposite: true,
          title: { text: '' },
        },
      ]

      const xAxis = { labels: { format: '{value:%Y}' }, type: 'category' }

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          series,
          xAxis,
          yAxis,
        },
        stockTools: true,
        useRegularChart: true,
      }
    }

    const convertDataToHighchartsFormat = (
      data: CompassAPIDataPoint[],
      options?: { convertPercentage: boolean }
    ) =>
      data.map(({ date, value }) => {
        const returnDate = new Date(date)
        const month = returnDate.getMonth() + 1
        const year = returnDate.getFullYear()
        return [
          `${month.toString().padStart(2, '0')}/${year}`,
          options?.convertPercentage ? 100 * value : value,
        ]
      })
  }

  export namespace Affordability {
    const CONTEXT_ID = 'equity:homebuilders:affordability'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'sac_vs_income'
        | 'affordability'
        | 'instalment_vs_income'
        | 'years_to_save'
        | 'loan_term_and_value'
        | 'interest_roe_spread'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'loan_term_and_value':
            return getLoanTermAndValueChartProps(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          ...(config.chart === 'interest_roe_spread'
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          tooltip: {
            pointFormatter: magnitudePointFormatter({
              unit: config.chart === 'interest_roe_spread' ? '%' : '',
            }),
            xDateFormat: '%Y',
          },
          xAxis: { labels: { format: '{value:%Y}' } },
        },
        {
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)

    const getLoanTermAndValueChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        convertToPercentages: [true, false],
        tooltip: { xDateFormat: '%Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
        yAxis: [
          { id: 'relative', labelSuffix: '%' },
          { id: 'absolute', opposite: true },
        ],
      },
      { type: ['spline', 'spline'], yAxis: ['relative', 'absolute'] }
    )
  }

  export namespace Targeting {
    const CONTEXT_ID = 'equity:homebuilders:targeting'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'funding_accum' | 'funding_accum_yoy'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          addMinMaxIndicators: true,
          ...(config.chart === 'funding_accum_yoy'
            ? {
                convertToPercentages: Array(data.data.length).fill(true),
              }
            : {}),
          tooltip: { xDateFormat: '%B %Y' },
          xAxis: { labels: { format: '{value:%Y}' } },
        })(config, data),
    }
  }

  export namespace Prices {
    const CONTEXT_ID = 'equity:homebuilders:prices'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'residential_prices_base_100'
        | 'incc_residential_prices_12m'
        | 'incc_residential_prices_24m'
        | 'residential_prices_rent'
        | 'residential_prices_sale'
        | 'ivgr'
        | 'igmi'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }

    const percentageCharts = [
      'incc_residential_prices_12m',
      'incc_residential_prices_24m',
    ]

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        ...(config.chart !== 'igmi' ? { addMinMaxIndicators: true } : {}),
        ...(percentageCharts.includes(config.chart)
          ? { convertToPercentages: Array(data.data.length).fill(true) }
          : {}),
        tooltip: {
          pointFormatter: magnitudePointFormatter({
            unit: percentageCharts.includes(config.chart) ? '%' : '',
          }),
          xDateFormat: '%B %Y',
          shared: config.chart === 'igmi',
          split: config.chart !== 'igmi',
        },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, data)
  }

  export namespace Fgts {
    const CONTEXT_ID = 'equity:homebuilders:fgts'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'discounts'
        | 'discounts_materialized_share'
        | 'discounts_materialized_share_accum'
        | 'discounts_predicted_materialized'
        | 'discounts_vgv_mcmv'
        | 'houses_mcmv'
        | 'discounts_vgv_ex_mcmv'
        | 'houses_ex_mcmv'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'discounts':
          case 'discounts_materialized_share':
          case 'discounts_materialized_share_accum':
            return getMultipleLinesStrCategoriesProps(config, data)
          case 'discounts_predicted_materialized':
          case 'discounts_vgv_mcmv':
          case 'discounts_vgv_ex_mcmv':
            return getMultipleMixedChartProps(config, data)
          case 'houses_mcmv':
          case 'houses_ex_mcmv':
            return getHousesMcmvChartProps(config, data)
        }
      },
    }

    const getMultipleLinesStrCategoriesProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleLineStrCategoriesChartProps(
        Context,
        {
          ...(config.chart !== 'discounts'
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          tooltip: {
            formatter: tooltipFormatterMinMaxMonthly(),
          },
        },
        {
          color: chartPalette.slice(0, data.data.length).reverse(),
          lineWidth: data.data.map((_, idx) =>
            idx === data.data.length - 1 ? 3 : 1.25
          ),
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)

    const getMultipleMixedChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: [false, false, true],
          tooltip: {
            xDateFormat: '%m/%Y',
          },
          xAxis: {
            labels: {
              format: '{value:%m/%Y}',
              rotation: -45,
            },
            tickPositions: data.data[0].data.map((point) =>
              new Date(point.date).getTime()
            ),
          },
          yAxis: [
            { id: 'absolute', title: { text: 'Bilhões' } },
            { id: 'relative', opposite: true, labelSuffix: '%' },
          ],
        },
        {
          color: [Palette.fadedBlue, Palette.fadedGreen, Palette.darkBlue],
          tooltip: ['bi', 'bi', '%'].map((unit) => ({
            pointFormatter: magnitudePointFormatter({ unit }),
          })),
          type: ['column', 'column', 'spline'],
          yAxis: ['absolute', 'absolute', 'relative'],
        }
      )(config, data)

    const getHousesMcmvChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          tooltip: {
            pointFormatter: magnitudePointFormatter({ unit: 'k' }),
            xDateFormat: '%m/%Y',
          },
          xAxis: {
            labels: {
              format: '{value:%m/%Y}',
              rotation: -45,
            },
            tickPositions: data.data[0].data.map((point) =>
              new Date(point.date).getTime()
            ),
          },
          yAxis: [{ id: 'absolute', title: { text: 'Quantidade (k)' } }],
        },
        { yAxis: ['absolute'] }
      )(config, data)
  }

  export namespace McmvFgts {
    const CONTEXT_ID = 'equity:homebuilders:mcmv_fgts'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'net_funding' | 'gross_funding' | 'withdrawal'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        id: `${config.chart}-${idx}`,
        type: idx === 0 || idx === 2 ? 'column' : 'spline',
        name: s.name,
        color:
          idx === 0
            ? Palette.fadedBlue
            : idx === 1
            ? Palette.darkBlue
            : idx === 2
            ? Palette.fadedGreen
            : Palette.darkGreen,
        yAxis: idx === 0 || idx === 2 ? 'absolute' : 'relative',
        data: convertDataToHighchartsFormat(s.data, {
          convertPercentage: idx === 1 || idx === 3,
        }),
        tooltip: {
          pointFormatter: magnitudePointFormatter({
            unit: idx === 0 || idx === 2 ? 'bi' : '%',
          }),
        },
        marker: { enabled: false },
        ...(idx === 0 || idx === 2 ? { pointPadding: 0, groupPadding: 0 } : {}),
      }))

      const yAxis = [
        {
          id: 'absolute',
          opposite: false,
          title: { text: 'Bilhões' },
        },
        {
          id: 'relative',
          opposite: true,
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          series,
          yAxis,
          xAxis: { type: 'category' },
        },
        stockTools: true,
        useRegularChart: true,
      }
    }

    const convertDataToHighchartsFormat = (
      data: CompassAPIDataPoint[],
      options?: { convertPercentage: boolean }
    ) =>
      data.map(({ date, value }) => {
        const returnDate = new Date(date)
        const month = returnDate.getMonth() + 1
        const year = returnDate.getFullYear()
        return [
          `${month.toString().padStart(2, '0')}/${year}`,
          options?.convertPercentage ? 100 * value : value,
        ]
      })
  }

  export namespace SecoviRmsp {
    const CONTEXT_ID = 'equity:homebuilders:secovi_rmsp'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'vso'
        | 'vso_12_months'
        | 'vendas_vgv'
        | 'lancamentos_vgv'
        | 'lancamentos_vgv_12_months'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'vso':
          case 'vendas_vgv':
          case 'lancamentos_vgv':
            return getMultipleLinesStrCategoriesProps(config, data)
          case 'vso_12_months':
            return getLineChartProps(config, data)
          case 'lancamentos_vgv_12_months':
            return getColumnLineChartProps(config, data)
        }
      },
    }

    const getMultipleLinesStrCategoriesProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleLineStrCategoriesChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          ...(config.chart === 'vso'
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          tooltip: {
            pointFormatter: magnitudePointFormatter({
              unit: config.chart === 'vso' ? '%' : '',
            }),
          },
        },
        {
          color: chartPalette.slice(0, data.data.length).reverse(),
          lineWidth: data.data.map((_, idx) =>
            idx === data.data.length - 1 ? 3 : 1.25
          ),
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)

    const getLineChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: [true],
        tooltip: { xDateFormat: '%B %Y' },
      })(config, data)

    const getColumnLineChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        color:
          idx === 0
            ? Palette.fadedBlue
            : idx === 1
            ? Palette.darkBlue
            : Palette.darkGreen,
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: idx !== 0,
        }),
        id: `${config.chart}-${idx}`,
        name: s.name,
        tooltip: {
          pointFormatter: magnitudePointFormatter({
            unit: idx === 0 ? '' : '%',
          }),
          xDateFormat: '%B %Y',
        },
        type: idx === 0 ? 'column' : 'line',
        yAxis: idx === 0 ? 'absolute' : 'relative',
        ...(idx === 0 ? { pointPadding: 0, groupPadding: 0 } : {}),
      }))

      const yAxis = [
        {
          id: 'absolute',
          opposite: false,
          title: { text: 'Milhão m²' },
        },
        {
          id: 'relative',
          opposite: true,
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        defaultIndicators: defaultMinMaxIndicators(series.slice(1)),
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          yAxis,
        },
        stockTools: true,
      }
    }
  }

  export namespace SecoviSp {
    const CONTEXT_ID = 'equity:homebuilders:secovi_sp'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'vso'
        | 'vso_12_months'
        | 'vendas_vgv'
        | 'lancamentos_vgv'
        | 'lancamentos_vgv_12_months'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'vso':
          case 'vendas_vgv':
          case 'lancamentos_vgv':
            return getMultipleLinesStrCategoriesProps(config, data)
          case 'vso_12_months':
            return getLineChartProps(config, data)
          case 'lancamentos_vgv_12_months':
            return getColumnLineChartProps(config, data)
        }
      },
    }

    const getMultipleLinesStrCategoriesProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleLineStrCategoriesChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          ...(config.chart === 'vso'
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          tooltip: {
            pointFormatter: magnitudePointFormatter({
              unit: config.chart === 'vso' ? '%' : '',
            }),
          },
        },
        {
          color: chartPalette.slice(0, data.data.length).reverse(),
          lineWidth: data.data.map((_, idx) =>
            idx === data.data.length - 1 ? 3 : 1.25
          ),
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)

    const getLineChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: [true],
        tooltip: { xDateFormat: '%B %Y' },
      })(config, data)

    const getColumnLineChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        color:
          idx === 0
            ? Palette.fadedBlue
            : idx === 1
            ? Palette.darkBlue
            : Palette.darkGreen,
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: idx !== 0,
        }),
        id: `${config.chart}-${idx}`,
        name: s.name,
        tooltip: {
          pointFormatter: magnitudePointFormatter({
            unit: idx === 0 ? '' : '%',
          }),
          xDateFormat: '%B %Y',
        },
        type: idx === 0 ? 'column' : 'line',
        yAxis: idx === 0 ? 'absolute' : 'relative',
        ...(idx === 0 ? { pointPadding: 0, groupPadding: 0 } : {}),
      }))

      const yAxis = [
        {
          id: 'absolute',
          opposite: false,
          title: { text: 'Milhão m²' },
        },
        {
          id: 'relative',
          opposite: true,
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        defaultIndicators: defaultMinMaxIndicators(series.slice(1)),
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          yAxis,
        },
        stockTools: true,
      }
    }
  }

  export namespace Abrainc {
    const CONTEXT_ID = 'equity:homebuilders:abrainc'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'lancamento_12_meses'
        | 'lancamentos_3_meses'
        | 'venda_unidades_3_meses'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'lancamento_12_meses':
            return getColumnLineChartProps(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }
    const getColumnLineChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        color:
          idx === 0
            ? Palette.fadedBlue
            : idx === 1
            ? Palette.darkBlue
            : Palette.darkGreen,
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: idx !== 0,
        }),
        id: `${config.chart}-${idx}`,
        type: idx === 0 ? 'column' : 'line',
        name: s.name,
        tooltip: {
          pointFormatter: magnitudePointFormatter({
            unit: idx === 0 ? '' : '%',
          }),
          xDateFormat: '%B %Y',
        },
        yAxis: idx === 0 ? 'absolute' : 'relative',
        ...(idx === 0 ? { pointPadding: 0, groupPadding: 0 } : {}),
      }))

      const yAxis = [
        {
          id: 'absolute',
          opposite: false,
          title: { text: 'Milhão m²' },
        },
        {
          id: 'relative',
          opposite: true,
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        defaultIndicators: defaultMinMaxIndicators(series.slice(1)),
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          yAxis,
        },
        stockTools: true,
      }
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: Array(data.data.length).fill(true),
        tooltip: { xDateFormat: '%B %Y' },
      })(config, data)
  }

  export namespace Confidence {
    const CONTEXT_ID = 'equity:homebuilders:confidence'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'fgv_construction_confidence_series'
        | 'fgv_consumer_confidence_series'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          tooltip: { xDateFormat: '%B %Y' },
        })(config, data),
    }
  }

  export namespace Costs {
    const CONTEXT_ID = 'equity:homebuilders:costs'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'monthly_rates' | 'accumulated'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          ...(config.chart === 'monthly_rates'
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          tooltip: { xDateFormat: '%B %Y' },
        })(config, data),
    }
  }

  export namespace CniConstructionConfidence {
    const CONTEXT_ID = 'equity:homebuilders:cni_construction_confidence'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      series: string[]
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (
      config: ChartConfig,
      modifier: RequestModifier = identity
    ) => {
      const response = (await axios(
        modifier({
          url:
            HOMEBUILDERS_CHARTS_PATH +
            '/confidence/cni-construction-confidence-series',
          method: 'POST',
          data: { series: config.series },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        { xAxis: datetimeXAxis[0] },
        { type: Array(data.data.length).fill('spline') }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Valuation {
    const CONTEXT_ID = 'equity:homebuilders:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'pe_cyre3'
        | 'pe_eztc3'
        | 'pe_mrve3'
        | 'pe_tend3'
        | 'pb_cyre3'
        | 'pb_eztc3'
        | 'pb_mrve3'
        | 'pb_tend3'
        | 'pe_cyre3_eztc3_mrve3_tend3'
        | 'pb_cyre3_eztc3_mrve3_tend3'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          ...(config.chart === 'pe_cyre3_eztc3_mrve3_tend3'
            ? {
                chartTitle: 'PE F12m Peers (Cyrela, Eztec, MRV e Tenda)',
              }
            : config.chart === 'pb_cyre3_eztc3_mrve3_tend3'
            ? {
                chartTitle: 'PB F12m Peers (Cyrela, Eztec, MRV e Tenda)',
              }
            : {}),
          ...(config.chart !== 'pe_cyre3_eztc3_mrve3_tend3' &&
          config.chart !== 'pb_cyre3_eztc3_mrve3_tend3'
            ? { msdIndicators: { avg: true, sd1: true, sd2: true } }
            : {}),
        },
        {
          ...(config.chart === 'pe_cyre3_eztc3_mrve3_tend3' ||
          config.chart === 'pb_cyre3_eztc3_mrve3_tend3'
            ? {
                name: [
                  'CYRE3 BS EQUITY',
                  'EZTC3 BS EQUITY',
                  'MRVE3 BS EQUITY',
                  'TEND3 BS EQUITY',
                ],
              }
            : { name: [''] }),
        }
      )(config, data)
  }
}
